import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useContext, useRef } from "react";
import ComplianceConnectService from "@app/compliance-connect/common/complianceConnectService";
import { ComplianceConnectNetwork } from "@app/compliance-connect/regulator/complianceConnectTypes";
import { ComplianceConnectFiltersContext } from "@app/compliance-connect/regulator/contexts/ComplianceConnectFiltersContext";
import { UserContext } from "@app/user/UserContext";

export interface UseRegulatorNetworksParams {
    pageIndex: number;
    pageSize: number;
    sortColumn?: string;
    sortDescending?: boolean;
}

interface UseRegulatorNetworksResult {
    regulatorNetworks: ComplianceConnectNetwork[] | null;
    regulatorNetworksError: boolean;
    isLoading: boolean;
    totalCount: number;
}

export function useRegulatorNetworks(params: UseRegulatorNetworksParams): UseRegulatorNetworksResult {
    const service = useRef(new ComplianceConnectService()).current;
    const { apiReadyFilters, isFilterSelectionEmpty } = useContext(ComplianceConnectFiltersContext);
    const user = useContext(UserContext);

    const { data, error, isPending } = useQuery({
        enabled: apiReadyFilters != null && !isFilterSelectionEmpty && user?.userId != null && user?.clientId != null,
        placeholderData: keepPreviousData,
        queryFn: ({ signal }) => service.getRegulatorNetworks({ ...apiReadyFilters, ...params }, signal),
        queryKey: ["cc-regulator-networks", user?.userId, user?.clientId, JSON.stringify(apiReadyFilters), params.pageIndex, params.pageSize, params.sortColumn, params.sortDescending],
        staleTime: 5 * 60 * 1000,
    });

    // An explicit "select nothing" is not sendable as an empty-array request (the backend can't tell
    // it apart from "select everything" — see ComplianceConnectFiltersContext's applyFilters comment),
    // so short-circuit client-side instead of firing a query that would come back as "match everything".
    if (isFilterSelectionEmpty) {
        return { isLoading: false, regulatorNetworks: [], regulatorNetworksError: false, totalCount: 0 };
    }

    return {
        isLoading: isPending,
        regulatorNetworks: data?.networks ?? null,
        regulatorNetworksError: error != null,
        totalCount: data?.totalCount ?? 0,
    };
}
